import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { HERO_SLIDES } from "../../data/content";
import { waLink } from "../../lib/brand";

export default function Hero() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="home-hero"
      data-on-dark
      className="relative min-h-[100svh] w-full overflow-hidden bg-ink"
    >
      {HERO_SLIDES.map((s, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${s.image})`,
              transform: i === idx ? "scale(1)" : "scale(1.06)",
              transition: "transform 8s ease-out",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/40" />
        </div>
      ))}

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-8 pt-36 md:pt-44 pb-24 md:pb-32 min-h-[100svh] flex flex-col justify-center">
        {/* Rotating small kicker */}
        <div
          key={`kicker-${i}`}
          className="fade-up flex items-center gap-4 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/70 mb-8"
        >
          <span className="h-[1px] w-8 bg-white/50" />
          {HERO_SLIDES[i].eyebrow}
        </div>

        {/* Static H1 (SEO) */}
        <h1 className="font-display text-white text-[42px] sm:text-6xl lg:text-[92px] leading-[0.95] tracking-[-0.035em] max-w-5xl">
          Professional
          <br />
          <span className="text-orange">Epoxy Flooring</span>,
          <br />
          Waterproofing &amp;{" "}
          <span className="font-serif-italic font-normal text-white/80">
            Painting
          </span>{" "}
          Services.
        </h1>

        <p className="fade-up mt-8 text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
          Complete flooring, waterproofing and painting solutions for homes,
          offices, commercial properties, factories and industrial spaces
          across Gurugram and Delhi NCR.
        </p>

        <div
          className="fade-up mt-10 flex flex-wrap items-center gap-4"
          style={{ animationDelay: "0.15s" }}
        >
          <Link
            to="/contact?service=inspection"
            data-testid="hero-inspection-button"
            className="group inline-flex items-center gap-3 bg-white hover:bg-white/90 text-ink px-8 py-4 font-medium tracking-[0.2em] text-[11px] uppercase transition-colors"
          >
            Get Free Site Inspection
            <ArrowRight
              size={14}
              className="transition-transform group-hover:translate-x-1"
            />
          </Link>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-button"
            className="inline-flex items-center gap-2 border border-white/40 hover:bg-white/10 text-white px-6 py-4 text-[11px] tracking-[0.2em] uppercase transition-colors"
          >
            <MessageCircle size={14} /> WhatsApp Us
          </a>
        </div>

        {/* Trust indicators */}
        <div
          className="fade-up mt-12 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-6 md:max-w-3xl border-t border-white/15 pt-8"
          style={{ animationDelay: "0.3s" }}
        >
          {[
            "Since 2019",
            "Professional Team",
            "Quality Materials",
            "Residential & Commercial",
          ].map((t) => (
            <div key={t} className="flex items-start gap-2">
              <span className="text-orange-light font-mono text-[10px] mt-1">
                ✓
              </span>
              <span className="text-white/85 text-xs md:text-sm">{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-8 left-5 md:left-8 z-10 flex items-center gap-2">
        {HERO_SLIDES.map((_, idx) => (
          <button
            key={idx}
            data-testid={`hero-slide-indicator-${idx}`}
            onClick={() => setI(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`h-[2px] transition-all duration-500 ${
              i === idx ? "w-12 bg-white" : "w-6 bg-white/30"
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-8 right-5 md:right-8 z-10 font-mono text-white/50 text-[10px] tracking-[0.25em] uppercase hidden md:block">
        {String(i + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
      </div>
    </section>
  );
}
